import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchRecent } from '../services/generatorBatchApi';

/**
 * Carica le macrofunzioni recenti da `/generator-batch/recent` applicando i
 * filtri server-side (since/until/prodotto/moduli/mie). La ricerca testuale è
 * lasciata al chiamante (filtro client). Ogni cambio filtri annulla la richiesta
 * precedente rimasta in volo (AbortController).
 *
 * @param {Object} params { productId, moduleIds, mine, since, until, limit }
 */
export function useRecentMacroFunctions(params) {
  const { productId, moduleIds, mine, since, until, limit = 100 } = params;
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Chiave stabile per i moduli (array) così da non rieseguire inutilmente.
  const moduleKey = Array.isArray(moduleIds) ? moduleIds.join(',') : '';

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);

    fetchRecent(
      {
        productId: productId || undefined,
        moduleIds: moduleKey ? moduleKey.split(',').map(Number) : undefined,
        mine: mine || undefined,
        since: since || undefined,
        until: until || undefined,
        limit,
      },
      controller.signal,
    )
      .then((data) => {
        setItems(Array.isArray(data?.items) ? data.items : []);
        setPagination(data?.pagination || null);
      })
      .catch((err) => {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        setError('Impossibile caricare le modifiche recenti.');
        setItems([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
    // moduleKey rappresenta moduleIds in forma stabile.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, moduleKey, mine, since, until, limit, reloadToken]);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  return { items, pagination, loading, error, reload };
}
